/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useContext} from 'react'
import {defineMessage, IntlContext} from 'react-intl'
import PropTypes from 'prop-types'
import {Icon, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '@salesforce/retail-react-app/app/assets/svg/account.svg'
import '@salesforce/retail-react-app/app/assets/svg/alert.svg'
import '../../assets/svg/apple.svg'
import '@salesforce/retail-react-app/app/assets/svg/basket.svg'
import '../../assets/svg/check.svg'
import '@salesforce/retail-react-app/app/assets/svg/check-circle.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-down.svg'
import '../../assets/svg/chevron-left.svg'
import '../../assets/svg/chevron-right.svg'
import '@salesforce/retail-react-app/app/assets/svg/chevron-up.svg'
import '@salesforce/retail-react-app/app/assets/svg/dashboard.svg'
import '@salesforce/retail-react-app/app/assets/svg/figma-logo.svg'
import '@salesforce/retail-react-app/app/assets/svg/filter.svg'
import '@salesforce/retail-react-app/app/assets/svg/file.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-ca.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-us.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-gb.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-fr.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-it.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-cn.svg'
import '@salesforce/retail-react-app/app/assets/svg/flag-jp.svg'
import '@salesforce/retail-react-app/app/assets/svg/github-logo.svg'
import '../../assets/svg/google.svg'
import '@salesforce/retail-react-app/app/assets/svg/hamburger.svg'
import '@salesforce/retail-react-app/app/assets/svg/info.svg'
import '../../assets/svg/social-facebook.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-instagram.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-twitter.svg'
import '@salesforce/retail-react-app/app/assets/svg/social-youtube.svg'
import '@salesforce/retail-react-app/app/assets/svg/store.svg'
import '@salesforce/retail-react-app/app/assets/svg/like.svg'
import '@salesforce/retail-react-app/app/assets/svg/lock.svg'
import '@salesforce/retail-react-app/app/assets/svg/plug.svg'
import '@salesforce/retail-react-app/app/assets/svg/plus.svg'
import '@salesforce/retail-react-app/app/assets/svg/receipt.svg'
import '@salesforce/retail-react-app/app/assets/svg/search.svg'
import '@salesforce/retail-react-app/app/assets/svg/signout.svg'
import '@salesforce/retail-react-app/app/assets/svg/user.svg'
import '../../assets/svg/visibility.svg'
import '../../assets/svg/visibility-off.svg'
import '../../assets/svg/heart.svg'
import '../../assets/svg/heart-solid.svg'
import '@salesforce/retail-react-app/app/assets/svg/close.svg'
import '../../assets/svg/recommended.svg'
import '../../assets/svg/thumbs-up.svg'
import '../../assets/svg/thumbs-up-dark.svg'
import '../../assets/svg/chevron-down-type2.svg'
import '../../assets/svg/custom-close.svg'
import '../../assets/svg/general-search.svg'
import '../../assets/svg/fis-back.svg'
import '../../assets/svg/starred-brand-logo.svg'
import '../../assets/svg/chevron-up-checkout.svg'
import '../../assets/svg/chevron-down-checkout.svg'
import '../../assets/svg/remove-gc.svg'
import '../../assets/svg/edit-pencil.svg'
import '../../assets/svg/notify-me-envelope.svg'
import '../../assets/svg/fit-icon.svg'
import '../../assets/svg/add-icon.svg'
import '../../assets/svg/apple-logo.svg'
import '../../assets/svg/apple-qr.svg'
import '../../assets/svg/android-logo.svg'
import '../../assets/svg/qv-close-icon.svg'
import '../../assets/svg/android-qr.svg'
import '../../assets/svg/check-circle-reverted.svg'
import '../../assets/svg/standard_shipping.svg'
import '../../assets/svg/express_shipping.svg'
import '../../assets/svg/uber_shipping.svg'
import '../../assets/svg/pickup-in-store_shipping.svg'
import '../../assets/svg/accordion-arrow.svg'
import '../../assets/svg/accordion-arrow-black.svg'
import '../../assets/svg/globe.svg'
import '../../assets/svg/globe-dark.svg'
import '../../assets/svg/mini-cart.svg'
import '../../assets/svg/profile.svg'
import '../../assets/svg/wishlist.svg'
import '../../assets/svg/header-search.svg'
import '../../assets/svg/header-menu.svg'
import '../../assets/svg/left-arrow.svg'
import '../../assets/svg/account-icon.svg'
import '../../assets/svg/loyalty-icon.svg'
import '../../assets/svg/gift-icon.svg'
import '../../assets/svg/account-drawer-logo.svg'
import '../../assets/svg/order-icon.svg'
import '../../assets/svg/logout-icon.svg'
import '../../assets/svg/dollar-icon.svg'
import '../../assets/svg/rewards-icon.svg'
import '../../assets/svg/applepay-logo.svg'
import '../../assets/svg/qr-code-icon.svg'
import '../../assets/svg/garage-brand.svg'
import '../../assets/svg/visa.svg'
import '../../assets/svg/amex.svg'
import '../../assets/svg/jcb.svg'
import '../../assets/svg/mastercard.svg'
import '../../assets/svg/unionpay.svg'
import '../../assets/svg/rewards-logo-desktop.svg'
import '../../assets/svg/rewards-logo-mobile.svg'
import '../../assets/svg/rewards-transaction.svg'
import '../../assets/svg/info-circle.svg'
import '../../assets/svg/sms-optin-icon.svg'
import '../../assets/svg/geolocation.svg'
import '../../assets/svg/play.svg'
import '../../assets/svg/pause.svg'
import '../../assets/svg/mute.svg'
import '../../assets/svg/unmute.svg'
import '../../assets/svg/share-icon.svg'
import '../../assets/svg/calendar.svg'
import '../../assets/svg/calendar-grey.svg'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AmexSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-amex.svg'
import BrandLogoSymbol from '../../assets/svg/brand-logo.svg'
import CVVSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-cvv.svg'
import DiscoverSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-discover.svg'
import LocationSymbol from '@salesforce/retail-react-app/app/assets/svg/location.svg'
import MastercardSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-mastercard.svg'
import PaypalSymbol from '@salesforce/retail-react-app/app/assets/svg/paypal.svg'
import SocialPinterestSymbol from '@salesforce/retail-react-app/app/assets/svg/social-pinterest.svg'
import VisaSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-visa.svg'
import GarageCollectifSymbol from '../../assets/svg/garage-collectif.svg'

// TODO: We're hardcoding the `viewBox` for these imported SVGs temporarily as the
// SVG loader plugin is not properly providing us the symbol data on the client side.
AmexSymbol.viewBox = '0 0 38 22'
BrandLogoSymbol.viewBox = '0 0 110 20' // Match the actual SVG viewBox to avoid hydration mismatch
CVVSymbol.viewBox = '0 0 41 24'
DiscoverSymbol.viewBox = '0 0 38 22'
LocationSymbol.viewBox = '0 0 16 21'
MastercardSymbol.viewBox = '0 0 38 22'
PaypalSymbol.viewBox = '0 0 80 20'
SocialPinterestSymbol.viewBox = '0 0 21 20'
VisaSymbol.viewBox = '0 0 38 22'
GarageCollectifSymbol.viewBox = '0 0 109 32'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 * @param {Object} passProps - props that will be passed onto the underlying Icon component
 * @param {Object} localizationAttributes - attributes with localized values that will be passed
 *      onto the underlying Icon component, use `defineMessage` to create localized string.
 *      Additionally, if the icon is rendered outside the provider tree, you'll also need to
 *      pass an intl object from react-intl as a prop to translate the messages.
 */
/* istanbul ignore next */
export const icon = (name, passProps, localizationAttributes) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const theme = useTheme()
        // NOTE: We want to avoid `useIntl` here because that throws when <IntlProvider> is not in
        // the component ancestry, but we only enforce `intl` if we have `localizationAttributes`.
        let intl = useContext(IntlContext)
        if (localizationAttributes) {
            if (props?.intl) {
                const {intl: intlProp, ...otherProps} = props
                // Allow `props.intl` to take precedence over the intl we found
                intl = intlProp
                props = otherProps
            }
            if (!intl) {
                throw new Error(
                    'To localize messages, you must either have <IntlProvider> in the component ancestry or provide `intl` as a prop'
                )
            }
            Object.keys(localizationAttributes).forEach((key) => {
                passProps[key] = intl.formatMessage(localizationAttributes[key])
            })
        }
        const baseStyle = theme?.components?.Icon?.baseStyle
        return (
            <Icon ref={ref} role="presentation" {...baseStyle} {...props} {...passProps}>
                <use xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        intl: PropTypes.object
    }

    component.displayName = `${displayName}Icon`
    return component
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const AccountIcon = icon('account')
export const AlertIcon = icon('alert')
export const AmexIcon = icon('cc-amex', {viewBox: AmexSymbol.viewBox})
export const AppleIcon = icon('apple')
export const BrandLogo = icon('brand-logo', {viewBox: BrandLogoSymbol.viewBox})
export const BasketIcon = icon('basket')
export const CheckIcon = icon('check')
export const CheckCircleIcon = icon('check-circle')
export const ChevronDownIcon = icon('chevron-down')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
export const ChevronUpIcon = icon('chevron-up')
export const CVVIcon = icon('cc-cvv', {viewBox: CVVSymbol.viewBox})
export const CloseIcon = icon('close')
export const CustomClose = icon('custom-close')
export const GeneralSearchIcon = icon('general-search')
export const MiniCartIcon = icon('mini-cart')
export const ProfileIcon = icon('profile')
export const WishlistIcon = icon('wishlist')
export const HeaderSearchIcon = icon('header-search')
export const HeaderMenuIcon = icon('header-menu')
export const FisBack = icon('fis-back')
export const DashboardIcon = icon('dashboard')
export const DiscoverIcon = icon('cc-discover', {viewBox: DiscoverSymbol.viewBox})
export const FigmaLogo = icon('figma-logo')
export const FilterIcon = icon('filter')
export const FileIcon = icon('file')
export const FlagCAIcon = icon('flag-ca')
export const FlagUSIcon = icon('flag-us')
export const FlagGBIcon = icon('flag-gb')
export const FlagFRIcon = icon('flag-fr')
export const FlagITIcon = icon('flag-it')
export const FlagCNIcon = icon('flag-cn')
export const FlagJPIcon = icon('flag-jp')
export const GithubLogo = icon('github-logo')
export const GoogleIcon = icon('google')
export const HamburgerIcon = icon('hamburger')
export const HeartIcon = icon('heart')
export const HeartSolidIcon = icon('heart-solid')
export const InfoIcon = icon('info')
export const LikeIcon = icon('like')
export const LockIcon = icon(
    'lock',
    {
        'aria-hidden': false,
        focusable: true
    },
    {
        'aria-label': defineMessage({
            id: 'icons.assistive_msg.lock',
            defaultMessage: 'Secure'
        })
    }
)
export const LocationIcon = icon('location')
export const PaypalIcon = icon('paypal', {viewBox: PaypalSymbol.viewBox})
export const PlugIcon = icon('plug')
export const PlusIcon = icon('plus')
export const MastercardIcon = icon('cc-mastercard', {viewBox: MastercardSymbol.viewBox})
export const ReceiptIcon = icon('receipt')
export const SearchIcon = icon('search', {'aria-hidden': true})
export const SocialFacebookIcon = icon('social-facebook')
export const SocialInstagramIcon = icon('social-instagram')
export const SocialPinterestIcon = icon('social-pinterest', {
    viewBox: SocialPinterestSymbol.viewBox
})
export const SocialTwitterIcon = icon('social-twitter')
export const SocialYoutubeIcon = icon('social-youtube')
export const StoreIcon = icon('store')
export const SignoutIcon = icon('signout')
export const UserIcon = icon('user')
export const VisaIcon = icon('cc-visa', {viewBox: VisaSymbol.viewBox})
export const VisibilityIcon = icon('visibility')
export const VisibilityOffIcon = icon('visibility-off')
export const Recommended = icon('recommended')
export const ThumbsUp = icon('thumbs-up')
export const ThumbsUpDark = icon('thumbs-up-dark')
export const ChevronDownType2 = icon('chevron-down-type2')
export const StarredBrandLogo = icon('starred-brand-logo', {viewBox: '0 0 43 8'})
export const ChevronUpCheckout = icon('chevron-up-checkout')
export const ChevronDownCheckout = icon('chevron-down-checkout')
export const RemoveGC = icon('remove-gc')
export const EditPencil = icon('edit-pencil')
export const NotifyMeEnvelope = icon('notify-me-envelope')
export const FitIcon = icon('fit-icon')
export const AddIcon = icon('add-icon')
export const AppleLogo = icon('apple-logo')
export const AppleQR = icon('apple-qr')
export const AndroidLogo = icon('android-logo')
export const QvCloseIcon = icon('qv-close-icon')
export const AndroidQR = icon('android-qr')
export const CheckCircleReverted = icon('check-circle-reverted')
export const StandardShipping = icon('standard_shipping')
export const ExpressShipping = icon('express_shipping', {viewBox: '0 0 36 24'})
export const UberShipping = icon('uber_shipping', {viewBox: '0 0 39 24'})
export const InStoreShipping = icon('pickup-in-store_shipping')
export const AccordionArrow = icon('accordion-arrow')
export const AccordionArrowBlack = icon('accordion-arrow-black')
export const Globe = icon('globe')
export const GlobeDark = icon('globe-dark')
export const GarageCollectif = icon('garage-collectif', {viewBox: GarageCollectifSymbol.viewBox})
export const LeftArrow = icon('left-arrow')
export const Account = icon('account-icon')
export const LoyaltyIcon = icon('loyalty-icon')
export const GiftIcon = icon('gift-icon')
export const AccountDrawerLogo = icon('account-drawer-logo')
export const OrderIcon = icon('order-icon')
export const LogoutIcon = icon('logout-icon')
export const DollarIcon = icon('dollar-icon')
export const RewardsIcon = icon('rewards-icon')
export const ApplepayIcon = icon('applepay-logo', {viewBox: '0 0 41 26'})
export const QRCodeIcon = icon('qr-code-icon')
export const GarageBrandIcon = icon('garage-brand', {viewBox: '0 0 43 8'})
export const Visa = icon('visa')
export const Amex = icon('amex')
export const Jcb = icon('jcb')
export const Mastercard = icon('mastercard')
export const UnionPay = icon('unionpay')
export const RewardsLogoDesktop = icon('rewards-logo-desktop', {viewBox: '0 0 240 69'})
export const RewardsLogoMobile = icon('rewards-logo-mobile', {viewBox: '0 0 170 49'})
export const RewardsTransaction = icon('rewards-transaction')
export const InfoCircle = icon('info-circle')
export const SMSOptInIcon = icon('sms-optin-icon')
export const Geolocation = icon('geolocation')
export const Play = icon('play')
export const Pause = icon('pause')
export const Mute = icon('mute')
export const Unmute = icon('unmute')
export const ShareIcon = icon('share-icon')
export const CalendarIcon = icon('calendar')
export const CalendarGreyIcon = icon('calendar-grey')
